// ══════════════════════════════════════════════════════════════
// COMPASS – Board Member Effects (US-041)
// ══════════════════════════════════════════════════════════════

import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { BoardMemberActions } from './board-member.actions';
import { ToastActions } from '../toast/toast.actions';
import { ApiConfiguration } from '../../api/api-configuration';
import { listBoardMembers } from '../../api/fn/management-company/list-board-members';
import { createBoardMember } from '../../api/fn/management-company/create-board-member';
import { updateBoardMember } from '../../api/fn/management-company/update-board-member';
import { deactivateBoardMember } from '../../api/fn/management-company/deactivate-board-member';
import { reactivateBoardMember } from '../../api/fn/management-company/reactivate-board-member';
import { errorMessage, readApiError } from '../../utils/api-error.utils';
import {
  DUPLICATE_BOARD_MEMBER_EMAIL_CODE,
  LAST_ACTIVE_BOARD_MEMBER_CODE,
  LAST_ACTIVE_BOARD_MEMBER_KEY,
} from './board-member.constants';

@Injectable()
export class BoardMemberEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiConfiguration);

  readonly loadBoardMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BoardMemberActions.loadBoardMembers),
      switchMap(({ managementCompanyId }) =>
        listBoardMembers(this.http, this.apiConfig.rootUrl, { managementCompanyId }).pipe(
          map((response) => BoardMemberActions.loadBoardMembersSuccess({ boardMembers: response.body ?? [] })),
          catchError((error) => [BoardMemberActions.loadBoardMembersFailure({
            error: errorMessage(readApiError(error), 'COMPANY.BOARD_MEMBERS.ERRORS.LOAD'),
          })]),
        ),
      ),
    ),
  );

  /** Create or edit. A failure carries the field errors for the dialog, which shows it (no toast here). */
  readonly saveBoardMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BoardMemberActions.saveBoardMember),
      switchMap(({ managementCompanyId, boardMemberId, body }) => {
        const request$ = boardMemberId
          ? updateBoardMember(this.http, this.apiConfig.rootUrl, { managementCompanyId, boardMemberId, body })
          : createBoardMember(this.http, this.apiConfig.rootUrl, { managementCompanyId, body });
        return request$.pipe(
          switchMap((response) => [
            BoardMemberActions.saveBoardMemberSuccess({ boardMember: response.body, created: !boardMemberId }),
            ToastActions.showToast({
              message: boardMemberId ? 'COMPANY.BOARD_MEMBERS.UPDATE_SUCCESS' : 'COMPANY.BOARD_MEMBERS.CREATE_SUCCESS',
              notificationType: 'success',
            }),
          ]),
          catchError((error) => {
            const apiError = readApiError(error);
            const message = errorMessage(apiError, 'COMPANY.BOARD_MEMBERS.ERRORS.SAVE');
            // A duplicate email is about the email field, so the dialog shows it there.
            const fieldErrors = apiError.code === DUPLICATE_BOARD_MEMBER_EMAIL_CODE
              ? { email: message }
              : apiError.fieldErrors;
            return [BoardMemberActions.saveBoardMemberFailure({ error: message, fieldErrors })];
          }),
        );
      }),
    ),
  );

  readonly deactivateBoardMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BoardMemberActions.deactivateBoardMember),
      // mergeMap: a second confirmation must not cancel the first one's response; the server applies both.
      mergeMap(({ managementCompanyId, boardMemberId }) =>
        deactivateBoardMember(this.http, this.apiConfig.rootUrl, { managementCompanyId, boardMemberId }).pipe(
          switchMap((response) => [
            BoardMemberActions.deactivateBoardMemberSuccess({ boardMember: response.body }),
            ToastActions.showToast({ message: 'COMPANY.BOARD_MEMBERS.DEACTIVATE_SUCCESS', notificationType: 'success' }),
          ]),
          catchError((error) => {
            const apiError = readApiError(error);
            // The list was out of date (another one was deactivated meanwhile): the text of the disabled action.
            const message = apiError.code === LAST_ACTIVE_BOARD_MEMBER_CODE
              ? LAST_ACTIVE_BOARD_MEMBER_KEY
              : errorMessage(apiError, 'COMPANY.BOARD_MEMBERS.ERRORS.DEACTIVATE');
            return [BoardMemberActions.deactivateBoardMemberFailure({ error: message, managementCompanyId })];
          }),
        ),
      ),
    ),
  );

  readonly reactivateBoardMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BoardMemberActions.reactivateBoardMember),
      mergeMap(({ managementCompanyId, boardMemberId }) =>
        reactivateBoardMember(this.http, this.apiConfig.rootUrl, { managementCompanyId, boardMemberId }).pipe(
          switchMap((response) => [
            BoardMemberActions.reactivateBoardMemberSuccess({ boardMember: response.body }),
            ToastActions.showToast({ message: 'COMPANY.BOARD_MEMBERS.REACTIVATE_SUCCESS', notificationType: 'success' }),
          ]),
          catchError((error) => [BoardMemberActions.reactivateBoardMemberFailure({
            error: errorMessage(readApiError(error), 'COMPANY.BOARD_MEMBERS.ERRORS.REACTIVATE'),
            managementCompanyId,
          })]),
        ),
      ),
    ),
  );

  /** After a refused deactivate or reactivate the list may be out of date: reload it. */
  readonly reloadAfterRefusedChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BoardMemberActions.deactivateBoardMemberFailure, BoardMemberActions.reactivateBoardMemberFailure),
      map(({ managementCompanyId }) => BoardMemberActions.loadBoardMembers({ managementCompanyId })),
    ),
  );

  readonly handleFailures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BoardMemberActions.loadBoardMembersFailure,
        BoardMemberActions.deactivateBoardMemberFailure,
        BoardMemberActions.reactivateBoardMemberFailure,
      ),
      map(({ error }) => ToastActions.showToast({ message: error, notificationType: 'error' })),
    ),
  );
}
